/**
 * JarvisSettings — Configuração da experiência Jarvis (voz) por organização.
 *  - Wake-words (lista de palavras de invocação)
 *  - Persona padrão (selecionada do catálogo de personas IA)
 *  - Modo padrão (passive / invocation / proactive)
 */
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useOrgRole } from "@/hooks/useOrgRole";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Mic, Plus, X, Loader2, Volume2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { JarvisVoicePicker } from "@/components/jarvis/JarvisVoicePicker";
import { JarvisVoiceCloneDialog } from "@/components/jarvis/JarvisVoiceCloneDialog";

type Mode = "passive" | "invocation" | "proactive";

interface OrgJarvis {
  jarvis_wake_words: string[];
  jarvis_default_persona_id: string | null;
  jarvis_default_mode: Mode;
  jarvis_default_voice_key: string | null;
}

export default function JarvisSettings() {
  const { orgId } = useOrgRole();
  const qc = useQueryClient();
  const [wakeInput, setWakeInput] = useState("");
  const [form, setForm] = useState<OrgJarvis>({
    jarvis_wake_words: ["jarvis", "syra"],
    jarvis_default_persona_id: null,
    jarvis_default_mode: "invocation",
    jarvis_default_voice_key: null,
  });

  const { data: org, isLoading } = useQuery({
    queryKey: ["org-jarvis", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("jarvis_wake_words, jarvis_default_persona_id, jarvis_default_mode, jarvis_default_voice_key")
        .eq("id", orgId!)
        .single();
      if (error) throw error;
      return data as OrgJarvis;
    },
  });

  const { data: personas = [] } = useQuery({
    queryKey: ["jarvis-personas", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("syra_ai_agents_safe")
        .select("id, name, jarvis_voice_provider, jarvis_voice_id")
        .eq("org_id", orgId!)
        .eq("active", true)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  useEffect(() => {
    if (org) {
      setForm({
        jarvis_wake_words: org.jarvis_wake_words?.length ? org.jarvis_wake_words : ["jarvis"],
        jarvis_default_persona_id: org.jarvis_default_persona_id,
        jarvis_default_mode: (org.jarvis_default_mode as Mode) ?? "invocation",
        jarvis_default_voice_key: org.jarvis_default_voice_key ?? null,
      });
    }
  }, [org]);

  const saveMut = useMutation({
    mutationFn: async (payload: OrgJarvis) => {
      const { error } = await supabase
        .from("organizations")
        .update(payload as any)
        .eq("id", orgId!);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["org-jarvis", orgId] });
      toast.success("Configurações Jarvis salvas");
    },
    onError: (e: any) => toast.error(`Erro ao salvar: ${e.message}`),
  });

  const addWakeWord = () => {
    const w = wakeInput.trim().toLowerCase();
    if (!w) return;
    if (form.jarvis_wake_words.includes(w)) {
      toast.info("Wake-word já existe");
      return;
    }
    if (w.length < 2 || w.length > 24) {
      toast.error("Use entre 2 e 24 caracteres");
      return;
    }
    setForm({ ...form, jarvis_wake_words: [...form.jarvis_wake_words, w] });
    setWakeInput("");
  };

  const removeWakeWord = (w: string) => {
    if (form.jarvis_wake_words.length <= 1) {
      toast.error("Pelo menos 1 wake-word é obrigatória");
      return;
    }
    setForm({ ...form, jarvis_wake_words: form.jarvis_wake_words.filter((x) => x !== w) });
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight font-headline">Configurações do Jarvis</h1>
        <p className="text-muted-foreground mt-1">
          Personalize o comportamento padrão do assistente de voz para sua organização.
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Carregando…
        </div>
      ) : (
        <Tabs defaultValue="comportamento" className="space-y-4">
          <TabsList>
            <TabsTrigger value="comportamento">Comportamento</TabsTrigger>
            <TabsTrigger value="vozes"><Volume2 className="w-3.5 h-3.5 mr-1.5" />Vozes</TabsTrigger>
          </TabsList>

          <TabsContent value="comportamento" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Mic className="w-5 h-5 text-primary" /> Wake-words
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Palavras que ativam o Jarvis no modo invocação. Pelo menos uma é obrigatória.
              </p>
              <div className="flex flex-wrap gap-2">
                {form.jarvis_wake_words.map((w) => (
                  <Badge key={w} variant="secondary" className="text-sm py-1.5 px-3 gap-2">
                    {w}
                    <button
                      type="button"
                      onClick={() => removeWakeWord(w)}
                      className="hover:text-destructive transition-colors"
                      aria-label={`Remover ${w}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  value={wakeInput}
                  onChange={(e) => setWakeInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addWakeWord(); } }}
                  placeholder="Ex: jarvis, syra, hey computer…"
                  className="max-w-sm"
                />
                <Button type="button" variant="outline" onClick={addWakeWord}>
                  <Plus className="w-4 h-4 mr-1" /> Adicionar
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Persona padrão</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Persona usada por padrão em novas sessões Jarvis. Configure voz e descrição na página{" "}
                <a href="/agents" className="text-primary underline">Personas IA</a>.
              </p>
              <div className="space-y-2">
                <Label>Persona</Label>
                <Select
                  value={form.jarvis_default_persona_id ?? "none"}
                  onValueChange={(v) =>
                    setForm({ ...form, jarvis_default_persona_id: v === "none" ? null : v })
                  }
                >
                  <SelectTrigger className="max-w-md">
                    <SelectValue placeholder="Sem persona padrão" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem persona (Jarvis genérico)</SelectItem>
                    {personas.map((p: any) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                        {p.jarvis_voice_provider ? ` · ${p.jarvis_voice_provider}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {personas.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    Nenhuma persona ativa. Crie uma em /agents.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Modo padrão</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Select
                value={form.jarvis_default_mode}
                onValueChange={(v) => setForm({ ...form, jarvis_default_mode: v as Mode })}
              >
                <SelectTrigger className="max-w-md">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="passive">Passivo — só escuta</SelectItem>
                  <SelectItem value="invocation">Invocação — responde com wake-word</SelectItem>
                  <SelectItem value="proactive">Proativo — pode intervir</SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button
              onClick={() => saveMut.mutate(form)}
              disabled={saveMut.isPending || !orgId}
              size="lg"
            >
              {saveMut.isPending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvando…</>
              ) : (
                "Salvar configurações"
              )}
            </Button>
          </div>
          </TabsContent>

          <TabsContent value="vozes" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Volume2 className="w-5 h-5 text-primary" /> Catálogo de vozes
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      Escolha a voz padrão da organização ou clone uma nova. Clique no play para ouvir uma prévia.
                    </p>
                  </div>
                  <JarvisVoiceCloneDialog orgId={orgId} />
                </div>
              </CardHeader>
              <CardContent>
                <JarvisVoicePicker
                  orgId={orgId}
                  value={form.jarvis_default_voice_key}
                  onChange={(key) => {
                    setForm((f) => ({ ...f, jarvis_default_voice_key: key }));
                    saveMut.mutate({ ...form, jarvis_default_voice_key: key });
                  }}
                />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
